import { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { FileText, EyeOff } from "lucide-react";

/**
 * Phase 71 — ResumeThumb
 *
 * A tiny (48×64px) inline thumbnail of a candidate's resume — first PDF page
 * rendered client-side via PDF.js. Lazy-loaded through an IntersectionObserver
 * so 30+ rows on the candidates list don't fire 30+ blob fetches until each
 * thumb is actually scrolled into view. Results are cached in a module-level
 * Map so scrolling away and back doesn't refetch.
 *
 * Fallbacks (in order):
 *   • Non-PDF file → generic file-icon badge with extension label.
 *   • Fetch/decode error → same file-icon badge, muted.
 *   • Candidate with no resume_filename → returns null (renders nothing).
 *
 * Clicking the thumb calls `onClick` (Prevents row-click when the caller
 * wraps in a stopPropagation container).
 */

// Module-level cache so re-mounts (e.g., row filter changes) skip re-render.
const CACHE = new Map(); // candidateId → { dataUrl?: string, ext?: string }
let pdfjsLoader = null;  // Promise<pdfjsLib> — resolved once and reused.

function loadPdfJs() {
    if (pdfjsLoader) return pdfjsLoader;
    pdfjsLoader = (async () => {
        const pdfjsLib = await import(/* webpackChunkName: "pdfjs" */ "pdfjs-dist");
        // Worker is served from our own /public/ folder (copied at build time
        // in /app/frontend/public/pdf.worker.min.mjs) so we don't depend on a
        // third-party CDN that may be blocked or rate-limited in some envs.
        pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";
        return pdfjsLib;
    })();
    return pdfjsLoader;
}

export default function ResumeThumb({ candidateId, filename, onClick, masked = false, size = 48 }) {
    const cid = candidateId;
    const [state, setState] = useState(() => (CACHE.get(cid) || { status: "idle" }));
    const ref = useRef(null);

    // Extension is used both as the fallback badge label AND to skip pdf.js
    // for known non-PDF files without downloading them.
    const ext = (filename || "").split(".").pop().toLowerCase();

    useEffect(() => {
        if (!cid || !filename) return () => {};
        // Already cached (success or non-pdf fallback) → nothing to do.
        const cached = CACHE.get(cid);
        if (cached) { setState({ status: cached.dataUrl ? "ready" : "fallback", ...cached }); return () => {}; }

        let cancelled = false;
        const io = new IntersectionObserver((entries) => {
            if (!entries[0].isIntersecting || cancelled) return;
            io.disconnect();
            setState({ status: "loading" });

            (async () => {
                try {
                    // Skip PDF.js entirely for known non-embeddable formats.
                    if (ext && !["pdf"].includes(ext)) {
                        const rec = { status: "fallback", ext };
                        CACHE.set(cid, { ext });
                        if (!cancelled) setState(rec);
                        return;
                    }

                    const url = masked
                        ? `/candidates/${cid}/masked-resume`
                        : `/candidates/${cid}/resume`;
                    const res = await api.get(url, { responseType: "arraybuffer" });
                    const ct = (res.headers["content-type"] || "").split(";")[0].trim().toLowerCase();
                    // If the backend served the resume as something other than
                    // PDF, we can't render a page image — fall back to badge.
                    if (ct && ct !== "application/pdf") {
                        const rec = { ext: ct.split("/")[1] || ext || "file" };
                        CACHE.set(cid, rec);
                        if (!cancelled) setState({ status: "fallback", ...rec });
                        return;
                    }

                    const pdfjsLib = await loadPdfJs();
                    if (cancelled) return;
                    const pdf = await pdfjsLib.getDocument({ data: new Uint8Array(res.data) }).promise;
                    const page = await pdf.getPage(1);
                    // Scale so the rendered width matches our display size × dpr
                    // — keeps the tiny image crisp on retina without wasting cycles.
                    const dpr = window.devicePixelRatio || 1;
                    const targetW = size * dpr;
                    const unscaled = page.getViewport({ scale: 1 });
                    const scale = targetW / unscaled.width;
                    const viewport = page.getViewport({ scale });

                    const canvas = document.createElement("canvas");
                    canvas.width = Math.ceil(viewport.width);
                    canvas.height = Math.ceil(viewport.height);
                    const ctx = canvas.getContext("2d", { alpha: false });
                    ctx.fillStyle = "#ffffff";
                    ctx.fillRect(0, 0, canvas.width, canvas.height);
                    // pdfjs-dist v4 removed the `background` param — call it
                    // clean and rely on the pre-painted white ctx.fillRect above.
                    await page.render({ canvasContext: ctx, viewport }).promise;
                    const dataUrl = canvas.toDataURL("image/jpeg", 0.72);
                    // Free memory — page/pdf keep large buffers alive otherwise.
                    try { page.cleanup(); pdf.destroy(); } catch { /* ignore */ }

                    const rec = { status: "ready", dataUrl };
                    CACHE.set(cid, { dataUrl });
                    if (!cancelled) setState(rec);
                } catch (e) {
                    // Surface via console.warn so future render regressions in
                    // pdfjs-dist (API removals, param renames) don't fail silently.
                    // eslint-disable-next-line no-console
                    console.warn("ResumeThumb render failed for candidate", cid, e);
                    const rec = { status: "fallback", ext: ext || "file" };
                    CACHE.set(cid, { ext: ext || "file" });
                    if (!cancelled) setState(rec);
                }
            })();
        }, { rootMargin: "150px" });

        if (ref.current) io.observe(ref.current);
        return () => { cancelled = true; io.disconnect(); };
    }, [cid, filename, ext, masked, size]);

    if (!cid || !filename) return null;

    // Aspect ratio 3:4 (US-letter portrait). 48×64px default.
    const box = { width: `${size}px`, height: `${Math.round(size * 4 / 3)}px` };

    // Backend requires cookies — direct <a href> also works for opening a new
    // tab because the API is same-origin (Cloudflare proxy strips subdomains).
    const openHref = masked
        ? `${API}/candidates/${cid}/masked-resume`
        : `${API}/candidates/${cid}/resume`;

    const inner = (() => {
        if (state.status === "ready") {
            return (
                <img
                    src={state.dataUrl}
                    alt={`${filename} — page 1`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                />
            );
        }
        if (state.status === "loading") {
            return (
                <div className="w-full h-full flex items-center justify-center bg-secondary/40 animate-pulse">
                    <FileText className="w-4 h-4 text-foreground/30" />
                </div>
            );
        }
        if (state.status === "fallback") {
            return (
                <div className="w-full h-full flex flex-col items-center justify-center bg-secondary/40 gap-0.5">
                    <FileText className="w-4 h-4 text-foreground/50" />
                    <span className="text-[8px] font-mono uppercase tracking-widest text-foreground/60">
                        {(state.ext || ext || "file").toUpperCase().slice(0, 4)}
                    </span>
                </div>
            );
        }
        // idle (pre-intersection)
        return <div className="w-full h-full bg-secondary/30" />;
    })();

    const commonCls = "block border border-border shrink-0 overflow-hidden bg-white hover:border-ikb transition-colors relative group";

    if (onClick) {
        return (
            <button
                ref={ref}
                type="button"
                onClick={(e) => { e.stopPropagation(); onClick(e); }}
                style={box}
                className={`${commonCls} cursor-zoom-in`}
                title={`Preview ${filename}`}
                data-testid={`resume-thumb-${cid}`}
                aria-label={`Preview resume for ${filename}`}
            >
                {inner}
                {masked && (
                    <span className="absolute top-0.5 left-0.5 bg-amber-100 border border-amber-300 rounded-sm p-0.5" title="Masked view">
                        <EyeOff className="w-2.5 h-2.5 text-amber-800" />
                    </span>
                )}
            </button>
        );
    }
    return (
        <a
            ref={ref}
            href={openHref}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={box}
            className={commonCls}
            title={`Open ${filename}`}
            data-testid={`resume-thumb-${cid}`}
        >
            {inner}
        </a>
    );
}
